import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { Company, CompanyCreateInput, CompanyUpdateInput } from '@/types';
import { logEvent } from './audit.service';

function col(tenantId: string) {
  return collection(db, `tenants/${tenantId}/companies`);
}

function docRef(tenantId: string, companyId: string) {
  return doc(db, `tenants/${tenantId}/companies/${companyId}`);
}

function fromFirestore(id: string, data: Record<string, unknown>): Company {
  return {
    ...data,
    id,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : String(data.createdAt ?? ''),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : String(data.updatedAt ?? ''),
  } as Company;
}

export const companyService = {
  async getAll(tenantId: string): Promise<Company[]> {
    const snap = await getDocs(query(col(tenantId), orderBy('name')));
    return snap.docs.map((d) => fromFirestore(d.id, d.data()));
  },

  async getById(tenantId: string, companyId: string): Promise<Company | null> {
    const snap = await getDoc(docRef(tenantId, companyId));
    if (!snap.exists()) return null;
    return fromFirestore(snap.id, snap.data());
  },

  async create(tenantId: string, input: CompanyCreateInput, performedBy: string): Promise<Company> {
    const now = serverTimestamp();
    const ref = await addDoc(col(tenantId), {
      ...input,
      tenantId,
      createdAt: now,
      updatedAt: now,
      createdBy: performedBy,
      updatedBy: performedBy,
    });
    const created = await this.getById(tenantId, ref.id);
    await logEvent({
      entityType: 'company',
      entityId: ref.id,
      action: 'create',
      tenantId,
      companyId: ref.id,
      performedBy,
      newValue: input as Record<string, unknown>,
    });
    return created!;
  },

  async update(tenantId: string, companyId: string, input: CompanyUpdateInput, performedBy: string): Promise<void> {
    await updateDoc(docRef(tenantId, companyId), {
      ...input,
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    await logEvent({
      entityType: 'company',
      entityId: companyId,
      action: 'update',
      tenantId,
      companyId,
      performedBy,
      newValue: input as Record<string, unknown>,
    });
  },

  async delete(tenantId: string, companyId: string, performedBy: string): Promise<void> {
    await updateDoc(docRef(tenantId, companyId), {
      status: 'inactive',
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    await logEvent({
      entityType: 'company',
      entityId: companyId,
      action: 'update',
      tenantId,
      companyId,
      performedBy,
      newValue: { status: 'inactive' },
    });
  },

  async uploadBranding(
    tenantId: string,
    companyId: string,
    file: File,
    uploadedBy: string,
  ): Promise<{ downloadUrl: string; storagePath: string }> {
    const ext = file.name.split('.').pop() ?? 'png';
    const fileName = `logo_${Date.now()}.${ext}`;
    const storagePath = `companies/${tenantId}/${companyId}/branding/${fileName}`;
    const storageRef = ref(storage, storagePath);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      customMetadata: { tenantId, companyId, uploadedBy },
    });
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', undefined, reject, resolve);
    });
    const downloadUrl = await getDownloadURL(task.snapshot.ref);
    return { downloadUrl, storagePath };
  },
};
